import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageBanner from '../components/PageBanner';
import { publicApi } from '../api/client';
import { Loader2, Clock, Check, X, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import BookingForm from '../components/BookingForm';
import { useSettings } from '../context/SettingsContext';

const PackageDetail = () => {
  const { slug } = useParams();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const settings = useSettings();

  useEffect(() => {
    setLoading(true);
    publicApi.getPackage(slug).then((d) => { setPkg(d); setLoading(false); }).catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="py-32 text-center"><Loader2 className="animate-spin inline text-[#304b71]" size={32} /></div>;
  if (!pkg) return <div className="py-32 text-center text-slate-500">Package not found. <Link to="/packages" className="text-[#304b71] underline">Back to packages</Link></div>;

  return (
    <>
      <PageBanner title={pkg.name} subtitle={pkg.short_desc} image={pkg.image} crumbs={[{ label: 'Packages', to: '/packages' }, { label: pkg.name }]} />

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <div className="flex flex-wrap gap-3 mb-6">
              <span className="bg-[#304b71] text-white px-3 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1"><Clock size={11} /> {pkg.days}</span>
              {pkg.tag && <span className="bg-[#f6f8fa] border border-slate-200 text-slate-700 px-3 py-1 rounded-full text-xs">{pkg.tag}</span>}
            </div>

            <h2 className="font-serif text-3xl text-[#304b71] mb-4">Overview</h2>
            <p className="text-slate-600 leading-relaxed whitespace-pre-line">{pkg.description}</p>

            {pkg.highlights?.length > 0 && (
              <div className="mt-10">
                <h2 className="font-serif text-2xl text-[#304b71] mb-4">Trip Highlights</h2>
                <ul className="grid md:grid-cols-2 gap-3">
                  {pkg.highlights.map((h, i) => <li key={i} className="flex items-start gap-2 text-slate-700"><Check size={18} className="text-[#6a8aa4] flex-shrink-0 mt-0.5" /> {h}</li>)}
                </ul>
              </div>
            )}

            {pkg.itinerary?.length > 0 && (
              <div className="mt-10">
                <h2 className="font-serif text-2xl text-[#304b71] mb-4">Day-by-Day Itinerary</h2>
                <div className="space-y-4">
                  {pkg.itinerary.map((d, i) => (
                    <div key={i} className="border-l-2 border-[#a6b2b5] pl-5 py-1">
                      <div className="text-xs uppercase tracking-widest text-[#6a8aa4]">{d.day}</div>
                      <h3 className="font-serif text-lg text-[#304b71] mt-1">{d.title}</h3>
                      <p className="text-slate-600 text-sm mt-1">{d.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(pkg.includes?.length > 0 || pkg.excludes?.length > 0) && (
              <div className="grid md:grid-cols-2 gap-8 mt-10">
                {pkg.includes?.length > 0 && (
                  <div>
                    <h3 className="font-serif text-xl text-[#304b71] mb-3">Included</h3>
                    <ul className="space-y-2">{pkg.includes.map((x, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><Check size={16} className="text-emerald-600 flex-shrink-0 mt-0.5" /> {x}</li>)}</ul>
                  </div>
                )}
                {pkg.excludes?.length > 0 && (
                  <div>
                    <h3 className="font-serif text-xl text-[#304b71] mb-3">Not Included</h3>
                    <ul className="space-y-2">{pkg.excludes.map((x, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><X size={16} className="text-rose-500 flex-shrink-0 mt-0.5" /> {x}</li>)}</ul>
                  </div>
                )}
              </div>
            )}
          </div>

          <aside className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-lg">
                <div className="text-xs uppercase tracking-widest text-slate-500">{pkg.price_label}</div>
                <div className="font-serif text-4xl text-[#304b71] mt-1">${pkg.price?.toLocaleString()}</div>
                <div className="text-xs text-slate-500">per person / USD</div>

                <div className="mt-6 border-t border-slate-100 pt-6">
                  <h3 className="font-serif text-lg text-[#304b71] mb-4">Enquire about this package</h3>
                  <BookingForm packageId={pkg.id} referenceName={pkg.name} defaultSubject={`Package: ${pkg.name}`} />
                </div>

                <div className="mt-6 pt-6 border-t border-slate-100 text-center">
                  <div className="text-xs text-slate-500 mb-2">Prefer to talk?</div>
                  <Button asChild variant="outline" className="w-full border-[#304b71] text-[#304b71] hover:bg-[#304b71] hover:text-white"><a href={`tel:${settings.phone_raw}`}><Phone size={14} className="mr-2" /> {settings.phone}</a></Button>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
};

export default PackageDetail;
